<template>
  <div class="std-background">
    <v-card width="500" class="mx-auto my-3">
      <div class="std-title std-title--no-bg text-center">
        <img :src="require('@/assets/consultation.png')" width="75">
        <h2><span class="std-title--small">Dr. Asmah</span><br/>Recommends a consultation first!</h2>
      </div>
      <div class="std__content" :class="{'ma-2 pa-3': $vuetify.breakpoint.smAndDown}">
        <p>Looks like {{representativeBooking ? 'she is' : 'you are'}} not quite ready for a 'Basic Bekam' therapy session. However, you can still contact Dr. Asmah for a consultation regarding {{representativeBooking ? 'her' : 'your'}} current condition.</p>
        <p>If {{representativeBooking ? 'she is' : 'you are'}} deemed fit for therapy, you can continue booking a session directly!</p>
        <h3 class="pb-5">Please enter your contact details</h3>
        <v-form v-model="valid">
          <v-text-field v-if="representativeBooking"
            outlined dense color="primaryLight" background-color="#fff"
            type="text" v-model="booker" label="Your name">
          </v-text-field>
          <v-text-field
            outlined dense color="primaryLight" background-color="#fff"
            type="text" v-model="name" :label="(representativeBooking ? 'Her ' : '') + 'Name'" :rules="nameRules"
            required>
          </v-text-field>
          <v-text-field v-if="representativeBooking"
            outlined dense color="primaryLight"
            type="text" v-model="relationship" label="She is your...">
          </v-text-field>
          <v-text-field
            outlined dense color="primaryLight" background-color="#fff"
            type="text" v-model="number" label="Phone/Mobile Number" :rules="numberRules"
            prefix="+673"
            required>
          </v-text-field>

          <!-- Diabetes consultation section -->
          <template v-if="diabetesConsultation">
            <hr class="rule">
            <p class="mb-0 mt-3">It seems {{representativeBooking ? 'she is' : 'you are'}} diabetic. Please select the option that best describes {{representativeBooking ? 'her' : 'you'}}.</p>
            <v-radio-group v-model="diabeticMedication" row mandatory>
              <v-radio
                class="mb-5"
                v-for="data in diabeticMedicationChecklist"
                :label="representativeBooking ? data.representativeLabel : data.selfLabel"
                :value="representativeBooking ? data.representativeLabel : data.selfLabel"
                :key="data.id">
              </v-radio>
            </v-radio-group>
            <p class="mb-0">{{representativeBooking ? 'Her' : 'My'}} blood sugar level before medication is around:</p>
            <v-slider
              class="mt-4"
              v-model="bloodSugarLevel"
              inverse-label
              min="3"
              max="8"
              step="0.1">
              <template v-slot:append>
                <p class="mb-0 primaryLight--text slider-label"><span class="primaryLight--text slider-label-numbering pr-2">{{bloodSugarLevel.toFixed(1)}}</span>mmol/L</p>
              </template>
            </v-slider>
          </template>

          <!-- Hypertension consultation section -->
          <template v-if="hypertensionConsultation">
            <hr class="rule">
            <p class="mb-3 mt-3">It seems {{representativeBooking ? 'she has' : 'you have'}} hypertension. Please select the option that best describes {{representativeBooking ? 'her' : 'you'}}.</p>
            <v-checkbox v-model="unsureBloodPressure" class="mt-0" :label="`I am unsure of ${representativeBooking ? 'her' : 'my'} blood pressure readings`"></v-checkbox>
            <template v-if="!unsureBloodPressure">
              <p class="mb-3">{{representativeBooking ? 'Her' : 'My'}} blood pressure level after medication is ranging around:</p>
              <v-range-slider
                class="mt-4 align-center"
                v-model="bloodPressureRange"
                max="140"
                min="60">
                <template v-slot:prepend>
                  <p class="mb-0 primaryLight--text slider-label text-center"><span><h1 class="mb-1">{{bloodPressureRange[0]}}</h1></span> mmHg</p>
                </template>
                <template v-slot:append>
                  <p class="mb-0 primaryLight--text slider-label text-center"><span><h1 class="mb-1">{{bloodPressureRange[1]}}</h1></span> mmHg</p>
                </template>
              </v-range-slider>
            </template>
          </template>

          <v-alert
            v-if="diabetesConsultation == null && hypertensionConsultation == null"
            outlined dense type="warning" class="py-5" prominent>
            <h3 class="warning--text">Seems like you have exited the page.</h3>
            <router-link to="/checklist" class="warning--text">Please retake the assessment!</router-link>
          </v-alert>

        </v-form>
        <div class="text-center mt-3 mb-10">
          <v-btn block depressed color="primaryLight" class="white--text"
            :loading="loading" :disabled="loading || !valid" @click="startConsultation()">
            Start {{representativeBooking ? 'her' : 'my'}} consultation
            <template v-slot:loader>
              <span>Processing...</span>
            </template>
          </v-btn>
        </div>

        <p v-if="errorMessage" class="red--text">{{errorMessage}}</p>
      </div> <!-- close content -->
    </v-card> <!-- close container -->
  </div>
</template>

<script>
  import apiService from '../services/api.service'

  export default {
    name: 'Consultation',
    data() {
      return {
        serviceNumber: 6738725714,
        representativeBooking: this.$store.state.representativeBooking,
        diabetesConsultation: this.$store.state.diabetesConsultation,
        hypertensionConsultation: this.$store.state.hypertensionConsultation,
        datepicker: false,
        valid: false,
        loading: false,

        // Form data
        booker: '',
        relationship: '',
        name: '',
        nameRules: [
          name => !!name || `What would ${this.representativeBooking ? 'she' : 'you'} like to be called?`
        ],
        number: '',
        numberRules: [
          number => !!number || 'Please enter a contact number.'
        ],
        diabeticMedicationChecklist: [
          { id: 1, selfLabel: 'I am taking oral or other medication', representativeLabel: 'She is taking oral or other medication' },
          { id: 2, selfLabel: 'I am taking insulin', representativeLabel: 'She is taking insulin' }
        ],
        diabeticMedication: null,
        bloodSugarLevel: 5.5,
        bloodPressureRange: [80, 120],
        unsureBloodPressure: false,
        errorMessage: null
      }
    },
    methods: {
      async startConsultation() {
        this.loading = true
        const diabetesCheck = this.diabetesConsultation ? {
          diabetic_medication: this.diabeticMedication,
          blood_sugar_level: this.bloodSugarLevel,
        } : {}
        const hypertensionCheck = this.hypertensionConsultation ? {
          unsure_blood_pressure: this.unsureBloodPressure,
          diastolic_bp: this.bloodPressureRange[0],
          systolic_bp: this.bloodPressureRange[1]
        } : {}
        await apiService.consult('sendConsultationMessage', {
          client: {
            name: this.name,
            contact_number: this.number,
            diabetes_check: diabetesCheck,
            hypertension_check: hypertensionCheck
          },
          booking: {
            is_self_booking: !this.representativeBooking,
            booker: this.booker || 'Self',
            relationship_with_booker: this.relationship || 'Self'
          }
        }).then(() => {
          this.loading = false
          this.$router.push('/results?status=amber')
        }).catch(err => {
          this.loading = false
          this.errorMessage = err.response.data
        })
      }
    }
  }
</script>

<style lang="scss">
  .slider-label {
    position: relative;
    top: -15px;

    .slider-label-numbering {
      font-size: 2em;
      font-weight: bold;
    }
  }
</style>
